import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Optional,
  Self,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NgControl,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { PlIcon } from '@app/icon-library/icons';
import { MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { isMoment } from '@app/utils/date';
import * as moment from 'moment';
import { Moment } from 'moment';

let identifier: number = 0;

export const MY_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'D MMM YYYY',
  },
  display: {
    dateInput: 'D MMM YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'D MMM YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'pl-datepicker',
  templateUrl: './pl-datepicker.component.html',
  styleUrls: ['./pl-datepicker.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }],
  encapsulation: ViewEncapsulation.None,
})
export class PlDatepickerComponent implements OnInit, ControlValueAccessor {
  @Input() public label: string = '';
  @Input() public isDisabled: boolean = false;
  @Input() public minDate: Moment | null = null;
  @Input() public maxDate: Moment | null = null;

  public value: Moment | null = null;

  public identifier: string = `pl-datepicker-${identifier++}`;

  private onChangeCallback!: (value: string | Moment | null) => void;
  private onTouchedCallback!: () => void;

  public readonly PlIcon: typeof PlIcon = PlIcon;

  @HostBinding('tabindex') public tabindex: number = 1;
  @HostListener('focus') public onFocus(): void {
    this.inputElement.nativeElement.focus({ preventScroll: true });
  }

  @ViewChild('inputElement', { read: ElementRef }) public inputElement!: ElementRef;

  constructor(@Optional() @Self() public controlDir: NgControl) {
    if (this.controlDir) {
      this.controlDir.valueAccessor = this;
    }
  }

  private modelTypeIsMoment: boolean = false;

  public ngOnInit(): void {
    if (this.controlDir) {
      const control: AbstractControl | null = this.controlDir.control;

      if (control) {
        const validators: ValidatorFn[] = control.validator ? [control.validator] : [];
        control.setValidators([this.validate.bind(this, validators)]);
        control.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  private validate(validators: ValidatorFn[]): ValidationErrors | null {
    const allErrors: any = {};
    for (const validator of validators) {
      const formControl: UntypedFormControl = new UntypedFormControl(this.value);
      const hasError: ValidationErrors | null = validator(formControl);
      if (hasError) {
        Object.entries(hasError).forEach(([errorName, isOnError]: [string, boolean]) => {
          allErrors[errorName] = isOnError;
        });
      }
    }

    if (Object.keys(allErrors).length > 0) {
      return allErrors;
    }

    return null;
  }

  public registerOnChange(fn: (value: string | Moment | null) => void): void {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public writeValue(valueToWrite: string | Moment | null): void {
    if (valueToWrite !== undefined) {
      if (isMoment(valueToWrite)) {
        this.modelTypeIsMoment = true;
      } else {
        this.modelTypeIsMoment = false;
      }

      this.value = moment(valueToWrite);
    }
    this.updateValueAndValidity();
  }

  public onBlur(): void {
    this.onTouched();
  }

  public onTouched(): void {
    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
  }

  public onChange(): void {
    this.emitChanges();
  }

  private emitChanges(): void {
    let valueToEmit: string | Moment | null = this.value;
    if (this.value !== null) {
      if (this.modelTypeIsMoment) {
        valueToEmit = this.value as Moment;
      } else {
        valueToEmit = this.value.format(`YYYY-MM-DD`);
      }
    }
    this.onChangeCallback(valueToEmit);
  }

  protected updateValueAndValidity(): void {
    if (this.controlDir.control) {
      this.controlDir.control.updateValueAndValidity({
        onlySelf: false,
        emitEvent: false,
      });
    }
  }
}
